// shared event-type vocabulary for the Events page and its drawer/legend

// plain-english label per event type; falls back to a humanized type string
export const EVENT_LABELS = {
  install_failed: 'Install failed',
  connection_error: 'Connection error',
  connection_created: 'Connection created',
  connection_reinstalled: 'Connection reinstalled',
  connection_deleted: 'Connection deleted',
  connection_disabled: 'Connection disabled',
  connection_enabled: 'Connection enabled',
  schedule_stuck: 'Schedule stuck',
  schedule_unstuck: 'Schedule recovered',
  schedule_paused: 'Schedule paused',
  schedule_unpaused: 'Schedule unpaused',
  on_demand_sync_stuck: 'On-demand sync stuck',
  on_demand_sync_started: 'On-demand sync started',
  on_demand_sync_completed: 'On-demand sync completed',
  on_demand_sync_cancelled: 'On-demand sync cancelled',
  historical_sync_completed: 'Historical sync completed',
};

// one plain-sentence definition per event type, grounded in the syncing-data docs
export const EVENT_DEFINITIONS = {
  install_failed: 'A customer tried to connect this integration and their credentials were rejected, so no connection was created.',
  connection_error: "A live connection's credentials or permissions broke, blocking or degrading its syncs.",
  connection_created: 'A new connection was successfully established.',
  connection_reinstalled: 'An existing connection was reconnected with new credentials.',
  connection_deleted: 'A connection was deleted.',
  connection_disabled: 'Every schedule on the connection was paused, so it stopped syncing.',
  connection_enabled: 'The connection started syncing again after being fully paused.',
  schedule_stuck: "A data type's recurring sync stopped making progress. Trackstar investigates automatically and it resumes once the issue is resolved.",
  schedule_unstuck: 'A stuck schedule started making progress again.',
  schedule_paused: "A data type's recurring sync was paused.",
  schedule_unpaused: "A data type's recurring sync was resumed.",
  on_demand_sync_stuck: "An on-demand sync stopped making progress and won't resume on its own — contact support to investigate.",
  on_demand_sync_started: 'A manually triggered (on-demand) sync began.',
  on_demand_sync_completed: 'A manually triggered sync finished successfully.',
  on_demand_sync_cancelled: 'A manually triggered sync was cancelled.',
  historical_sync_completed: "A data type's first (historical) sync finished.",
};

// severity tier per event type, for the legend's representative chip color
export const SEVERITY_BY_TYPE = {
  install_failed: 'error',
  connection_error: 'error',
  schedule_stuck: 'warning',
  on_demand_sync_stuck: 'warning',
  connection_created: 'info',
  connection_reinstalled: 'info',
  connection_deleted: 'info',
  connection_disabled: 'info',
  connection_enabled: 'info',
  schedule_unstuck: 'info',
  schedule_paused: 'info',
  schedule_unpaused: 'info',
  on_demand_sync_started: 'info',
  on_demand_sync_completed: 'info',
  on_demand_sync_cancelled: 'info',
  historical_sync_completed: 'info',
};

// every event type, ordered important-first, for the type picker and legend
export const EVENT_TYPES = [
  'install_failed', 'connection_error', 'schedule_stuck', 'on_demand_sync_stuck',
  'connection_created', 'connection_reinstalled', 'connection_deleted',
  'connection_disabled', 'connection_enabled',
  'schedule_paused', 'schedule_unpaused', 'schedule_unstuck',
  'on_demand_sync_started', 'on_demand_sync_completed', 'on_demand_sync_cancelled',
  'historical_sync_completed',
];

export function eventLabel(type) {
  if (EVENT_LABELS[type]) return EVENT_LABELS[type];
  return type.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

export function eventDefinition(type) {
  return EVENT_DEFINITIONS[type] || '';
}

// category per event type, for grouping the type filter and legend
export const EVENT_CATEGORIES = ['Install', 'Connection', 'Schedule', 'On-demand sync', 'Historical sync'];

export const EVENT_CATEGORY_BY_TYPE = {
  install_failed: 'Install',
  connection_error: 'Connection',
  connection_created: 'Connection',
  connection_reinstalled: 'Connection',
  connection_deleted: 'Connection',
  connection_disabled: 'Connection',
  connection_enabled: 'Connection',
  schedule_stuck: 'Schedule',
  schedule_unstuck: 'Schedule',
  schedule_paused: 'Schedule',
  schedule_unpaused: 'Schedule',
  on_demand_sync_started: 'On-demand sync',
  on_demand_sync_completed: 'On-demand sync',
  on_demand_sync_cancelled: 'On-demand sync',
  on_demand_sync_stuck: 'On-demand sync',
  historical_sync_completed: 'Historical sync',
};

export function eventCategory(type) {
  return EVENT_CATEGORY_BY_TYPE[type] || 'Other';
}

// EVENT_TYPES reordered so each category is contiguous, which is what a grouped
// dropdown/legend needs (grouping wants same-category items adjacent)
export const EVENT_TYPES_BY_CATEGORY = EVENT_CATEGORIES.flatMap(
  (category) => EVENT_TYPES.filter((type) => eventCategory(type) === category),
);

// severity -> presentation, shared by the Events page and the homepage feed
export function severityChipClass(severity) {
  if (severity === 'error') return 'badge-error';
  if (severity === 'warning') return 'badge-warning';
  return 'badge-neutral';
}

export function severityDotClass(severity) {
  if (severity === 'error') return 'status-dot-error';
  if (severity === 'warning') return 'status-dot-warning';
  return 'status-dot-neutral';
}

export function severityRailColor(severity) {
  if (severity === 'error') return '#DC2626';
  if (severity === 'warning') return '#D97706';
  return '#E7E5E4';
}
